'use client';

import type { ComponentProps } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cn } from '@ifixit/ui/lib/utils';

export function AlertDialog({ ...props }: ComponentProps<typeof AlertDialogPrimitive.Root>) {
   return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

export function AlertDialogTrigger({
   ...props
}: ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
   return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

export function AlertDialogPortal({
   ...props
}: ComponentProps<typeof AlertDialogPrimitive.Portal>) {
   return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

export function AlertDialogOverlay({
   className,
   ...props
}: ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
   return (
      <AlertDialogPrimitive.Overlay
         data-slot="alert-dialog-overlay"
         // z-dialog-overlay sits one tier below z-dialog so content-above-overlay
         // is encoded in the values, not sibling render order. Content stays at
         // z-dialog (50) to remain coplanar with popovers/selects/dropdowns
         // portaled into the body while a dialog is open. The backdrop still sits
         // above page chrome at z-40 (scroll-locked toolbar, drawers) so it dims
         // over them.
         className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-dialog-overlay bg-black/50',
            className
         )}
         {...props}
      />
   );
}

export function AlertDialogContent({
   className,
   overlayClassName,
   ...props
}: ComponentProps<typeof AlertDialogPrimitive.Content> & {
   /** Extra classes for the backdrop, e.g. a z-index override when the dialog
    * must stack above fixed page chrome that sits over the default
    * `z-dialog-overlay`. When overriding, keep the overlay below `z-dialog`. */
   overlayClassName?: string;
}) {
   return (
      <AlertDialogPortal>
         <AlertDialogOverlay className={overlayClassName} />
         <AlertDialogPrimitive.Content
            data-slot="alert-dialog-content"
            className={cn(
               'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-dialog grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-2xl border p-6 shadow-lg duration-200 sm:max-w-lg',
               className
            )}
            {...props}
         />
      </AlertDialogPortal>
   );
}

export function AlertDialogHeader({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="alert-dialog-header"
         className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
         {...props}
      />
   );
}

export function AlertDialogFooter({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="alert-dialog-footer"
         className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
         {...props}
      />
   );
}

export function AlertDialogTitle({
   className,
   ...props
}: ComponentProps<typeof AlertDialogPrimitive.Title>) {
   return (
      <AlertDialogPrimitive.Title
         data-slot="alert-dialog-title"
         className={cn('text-lg leading-none font-semibold', className)}
         {...props}
      />
   );
}

export function AlertDialogDescription({
   className,
   ...props
}: ComponentProps<typeof AlertDialogPrimitive.Description>) {
   return (
      <AlertDialogPrimitive.Description
         data-slot="alert-dialog-description"
         className={cn('text-muted-foreground text-sm', className)}
         {...props}
      />
   );
}

export function AlertDialogAction({
   className,
   ...props
}: ComponentProps<typeof AlertDialogPrimitive.Action>) {
   return (
      <AlertDialogPrimitive.Action
         data-slot="alert-dialog-action"
         className={cn('button-primary button-md', className)}
         {...props}
      />
   );
}

export function AlertDialogCancel({
   className,
   ...props
}: ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
   return (
      <AlertDialogPrimitive.Cancel
         data-slot="alert-dialog-cancel"
         className={cn('button-outline button-md', className)}
         {...props}
      />
   );
}
